import { type ReactNode } from "react";
import { openCookiePreferences } from "@/lib/cookie-consent";
import { Link } from "wouter";
import { ExternalLink } from "lucide-react";

import { useI18n } from "@/hooks/use-i18n";
import { useLocalizedUrl } from "@/hooks/use-localized-url";
import { trackDonateClick } from "@/lib/analytics";
import { MAIN_NAV } from "@/components/layout/header/nav-data";
import { NewsletterForm } from "@/components/layout/footer/NewsletterForm";
import { SocialIcons } from "@/components/layout/footer/FooterSocialIcons";

// Footer scuro (#202A2E, antracite comune a Trieste) con testo bianco attenuato su tre livelli:
// 94% titoli, 68% testo e link, 46% note. Tutto Tailwind: è guscio, fuori dal ChakraBoundary di App.tsx.

function FooterLink({ href, children, external, onClick }: { href: string; children: ReactNode; external?: boolean; onClick?: () => void }) {
  if (external) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        className="inline-flex items-center gap-1.5 text-sm text-white/[.68] transition-colors hover:text-white/[.94]"
      >
        {children}
        <ExternalLink className="h-[13px] w-[13px] text-white/[.46]" strokeWidth={1.75} />
      </a>
    );
  }
  return (
    <Link href={href} onClick={onClick} className="text-sm text-white/[.68] transition-colors hover:text-white/[.94]">
      {children}
    </Link>
  );
}

export default function Footer() {
  const { t, language } = useI18n();
  const getLocalizedUrl = useLocalizedUrl();
  const currentYear = new Date().getFullYear();

  // Stesse voci del menu principale, da nav-data. Le audioguide non esistono in sloveno; il quiz non è nel footer.
  const navFooterItems = MAIN_NAV.flatMap((entry) => (entry.kind === "menu" ? entry.menu.items : [entry.link]))
    .filter((item) => !("requiresAudioguides" in item && item.requiresAudioguides) || language !== "sl")
    .filter((item) => !("italianOnly" in item && item.italianOnly));

  const navLinks = (
    <>
      <FooterLink href={getLocalizedUrl("/")}>{t("nav.home")}</FooterLink>
      {navFooterItems.map((item) => (
        <FooterLink key={item.key} href={getLocalizedUrl(item.path)}>{t(item.labelKey)}</FooterLink>
      ))}
    </>
  );

  const resourceLinks = (
    <>
      <FooterLink href="https://www.facebook.com/TriesteStorica/" external>{t("footer.facebookCommunity")}</FooterLink>
      <FooterLink href={getLocalizedUrl("/sostienici")} onClick={() => trackDonateClick('footer')}>
        <span className="inline-flex items-center gap-1.5">
          <img src="/img/kofi-cup-logo.png" alt="" width={16} height={16} loading="lazy" className="h-4 w-4 object-contain" />
          {t("footer.support")}
        </span>
      </FooterLink>
      <FooterLink href="https://www.comune.trieste.it" external>{t("footer.tourism")}</FooterLink>
    </>
  );

  const legalItems = [
    { href: getLocalizedUrl("/privacy"), label: t("footer.privacy") },
    { href: getLocalizedUrl("/terms"), label: t("footer.terms") },
    { href: getLocalizedUrl("/cookies"), label: t("footer.cookies") },
    { href: getLocalizedUrl("/licenses"), label: t("footer.licenses") },
  ];

  return (
    <footer className="bg-footer text-white/[.68]">
      <div className="mx-auto max-w-screen-xl px-4 md:px-6 pb-6 pt-12">
        <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:gap-x-8 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div className="col-span-2 lg:col-span-1">
            <NewsletterForm />
          </div>

          <nav className="flex flex-col items-start gap-2.5" aria-label={t("footer.navigation")}>
            <h3 className="mb-1 text-base font-semibold leading-snug text-white/[.94]">{t("footer.navigation")}</h3>
            {navLinks}
          </nav>

          <nav className="flex flex-col items-start gap-2.5" aria-label={t("footer.resources")}>
            <h3 className="mb-1 text-base font-semibold leading-snug text-white/[.94]">{t("footer.resources")}</h3>
            {resourceLinks}
          </nav>

          <div className="col-span-2 lg:col-span-1">
            <h3 className="mb-3.5 text-base font-semibold leading-snug text-white/[.94]">{t("footer.contact")}</h3>
            <SocialIcons />
          </div>
        </div>
      </div>

      {/* Riga di chiusura: filo sottile, testo al livello più tenue */}
      <div className="mx-auto max-w-screen-xl px-4 md:px-6">
        <div className="border-t border-white/[.09] py-5">
          <div className="flex flex-col items-center justify-center gap-3 md:flex-row md:justify-between">
            <div className="flex items-center justify-center gap-2">
              <span className="text-[13px] text-white/[.46]">{t("footer.projectCreatedBy")}</span>
              <a href="https://www.linkedin.com/company/therc/" target="_blank" rel="noopener noreferrer" className="group">
                <picture>
                  <source srcSet="/img/logo_theRC_optimized.webp" type="image/webp" />
                  <img
                    src="/img/logo_theRC_optimized.png"
                    srcSet="/img/logo_theRC_optimized.png 1x, /img/logo_theRC_optimized_2x.png 2x"
                    alt="theRC - Digital Innovation Company"
                    className="h-[22px] w-[52px] opacity-70 brightness-0 invert transition-opacity group-hover:opacity-100 md:h-7 md:w-[67px]"
                    loading="lazy"
                  />
                </picture>
              </a>
            </div>
            <div className="flex flex-col items-center gap-2 md:flex-row md:gap-4">
              <div className="flex flex-wrap items-center justify-center">
                {legalItems.map((item, i) => (
                  <span key={item.href} className="flex items-center">
                    <Link href={item.href} className="text-[13px] text-white/[.46] transition-colors hover:text-white/[.86]">{item.label}</Link>
                    <span className="mx-2 text-[13px] text-white/[.32]">·</span>
                  </span>
                ))}
                {/* Riapre il banner: la scelta sui cookie si deve poter cambiare in ogni momento */}
                <button type="button" onClick={openCookiePreferences}
                  className="text-[13px] text-white/[.46] transition-colors hover:text-white/[.86]">
                  {t("footer.cookiePreferences")}
                </button>
              </div>
              <span className="text-center text-[13px] text-white/[.46]">© {currentYear} Trieste Storica</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
